// src/pages/Profile.js — UI Pro Moderne v2
import { useEffect, useState, useCallback, useRef } from "react";
import {
  Box, Paper, Stack, Typography, Divider, Avatar,
  Chip, Button, TextField, Alert, CircularProgress,
  IconButton, Tooltip, LinearProgress,
} from "@mui/material";
import EditIcon           from "@mui/icons-material/Edit";
import SaveIcon           from "@mui/icons-material/Save";
import CloseIcon          from "@mui/icons-material/Close";
import ContentCopyIcon    from "@mui/icons-material/ContentCopy";
import BadgeIcon          from "@mui/icons-material/Badge";
import EmailIcon          from "@mui/icons-material/Email";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import TimerIcon          from "@mui/icons-material/Timer";
import CalendarMonthIcon  from "@mui/icons-material/CalendarMonth";
import { useTranslation } from "react-i18next";
import API, { getUserMessage } from "../api/axios";
import { fmtLocal }       from "../utils/datetime";

// ── Helpers ──────────────────────────────────────────────────────────────────
function InfoRow({ icon, label, value, mono = false, copyable = false, t }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard?.writeText(String(value || ""));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ py: 1.5 }}>
      <Box sx={{ color: "text.secondary", mt: 0.25 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: .5 }}>
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" fontWeight={500} sx={{ fontFamily: mono ? "monospace" : "inherit", wordBreak: "break-all" }}>
            {value || "—"}
          </Typography>
          {copyable && value && (
            <Tooltip title={copied ? (t ? t('profile.copied') : "Copié !") : (t ? t('profile.copy') : "Copier")}>
              <IconButton size="small" onClick={handleCopy} sx={{ opacity: .5, "&:hover": { opacity: 1 } }}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

function CreditBar({ balance, quota }) {
  const { t } = useTranslation();
  if (!quota) return null;
  const pct = Math.min(100, Math.max(0, (balance / quota) * 100));
  const color = pct > 50 ? "#22c55e" : pct > 20 ? "#f59e0b" : "#ef4444";
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">{t('profile.credits_available_label')}</Typography>
        <Typography variant="caption" fontWeight={700} sx={{ color }}>
          {balance?.toLocaleString()} / {quota?.toLocaleString()} min
        </Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct} sx={{
        height: 8, borderRadius: 4,
        bgcolor: "#f1f5f9",
        "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 4 },
      }} />
    </Box>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function Profile() {
  const { t } = useTranslation();
  const mountedRef = useRef(true);
  const [profile,  setProfile]  = useState(null);
  const [credits,  setCredits]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [editing,  setEditing]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [fullName, setFullName] = useState("");
  const [saveMsg,  setSaveMsg]  = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [pRes, cRes] = await Promise.allSettled([
        API.get("/users/me"),
        API.get("/credits/balance"),
      ]);
      if (!mountedRef.current) return;
      if (pRes.status === "fulfilled") {
        setProfile(pRes.value.data);
        setFullName(pRes.value.data?.full_name || pRes.value.data?.name || "");
      } else {
        setError(getUserMessage(pRes.reason, t('profile.load_error')));
      }
      if (cRes.status === "fulfilled") setCredits(cRes.value.data);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => { mountedRef.current = false; };
  }, [load]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await API.put("/users/me", { full_name: fullName });
      setProfile(p => ({ ...p, full_name: fullName }));
      setEditing(false);
      setSaveMsg(t('profile.updated'));
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (e) {
      setError(getUserMessage(e, t('profile.update_error')));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const displayName = profile?.full_name || profile?.name || profile?.username || profile?.sub || "Utilisateur";
  const initials    = displayName.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  const roleLabel   = (profile?.role || "user").toUpperCase();
  const roleColor   = profile?.role === "admin" ? "#6366f1" : "#64748b";

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", my: 4, px: { xs: 2, md: 3 } }}>

      {error    && <Alert severity="error"   onClose={() => setError("")}   sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
      {saveMsg  && <Alert severity="success" onClose={() => setSaveMsg("")} sx={{ mb: 2, borderRadius: 2 }}>{saveMsg}</Alert>}

      {/* Carte profil principale */}
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden", mb: 3 }}>

        {/* Bandeau header */}
        <Box sx={{
          height: 80,
          background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%)",
        }} />

        <Box sx={{ px: 3, pb: 3 }}>
          {/* Avatar + actions */}
          <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ mt: -4, mb: 2 }}>
            <Avatar sx={{
              width: 72, height: 72, fontSize: 24, fontWeight: 700,
              bgcolor: "#6366f1", border: "3px solid white",
              boxShadow: "0 2px 12px rgba(99,102,241,.3)",
            }}>
              {initials}
            </Avatar>
            {!editing ? (
              <Button size="small" startIcon={<EditIcon />} variant="outlined"
                onClick={() => setEditing(true)}
                sx={{ borderRadius: 2, mb: 1 }}>
{t('profile.edit')}
              </Button>
            ) : (
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button size="small" startIcon={<CloseIcon />} variant="outlined" color="inherit"
                  onClick={() => { setEditing(false); setFullName(profile?.full_name || ""); }}
                  sx={{ borderRadius: 2 }}>
{t('common.cancel')}
                </Button>
                <Button size="small" startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
                  variant="contained" onClick={handleSave} disabled={saving}
                  sx={{ borderRadius: 2, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
{t('profile.save')}
                </Button>
              </Stack>
            )}
          </Stack>

          {/* Nom + rôle */}
          {editing ? (
            <TextField
              size="small" label={t('profile.full_name')} value={fullName}
              onChange={e => setFullName(e.target.value)}
              sx={{ mb: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          ) : (
            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
              {displayName}
            </Typography>
          )}
          <Chip size="small" label={roleLabel}
            sx={{ bgcolor: roleColor + "20", color: roleColor, fontWeight: 700, fontSize: 11, height: 22 }} />

          <Divider sx={{ my: 2 }} />

          {/* Infos */}
          <InfoRow icon={<BadgeIcon fontSize="small" />} label={t('profile.identifier')} t={t}
            value={profile?.username || profile?.sub} mono copyable />
          <InfoRow icon={<EmailIcon fontSize="small" />} label={t('profile.email')} t={t}
            value={profile?.email} copyable />
          <InfoRow icon={<WorkspacePremiumIcon fontSize="small" />} label={t('profile.role')}
            value={roleLabel} />
          {profile?.created_at && (
            <InfoRow icon={<CalendarMonthIcon fontSize="small" />} label={t('profile.member_since')}
              value={fmtLocal(profile.created_at)} />
          )}
        </Box>
      </Paper>

      {/* Crédits */}
      {credits && (
        <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, p: 3, mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <TimerIcon sx={{ fontSize: 18, color: "#6366f1" }} />
            <Typography variant="subtitle1" fontWeight={600}>{t('profile.credits_title')}</Typography>
          </Stack>
          <CreditBar
            balance={credits.balance ?? credits.remaining ?? credits.credits}
            quota={credits.quota ?? credits.total ?? null}
          />
          <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap">
            {credits.balance !== undefined && (
              <Box sx={{ textAlign: "center", flex: 1, minWidth: 100 }}>
                <Typography variant="h6" fontWeight={700} color="#22c55e">
                  {Number(credits.balance ?? credits.remaining ?? 0).toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">{t('profile.credits_available')}</Typography>
              </Box>
            )}
            {credits.used !== undefined && (
              <Box sx={{ textAlign: "center", flex: 1, minWidth: 100 }}>
                <Typography variant="h6" fontWeight={700} color="#64748b">
                  {Number(credits.used ?? 0).toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">{t('profile.credits_used')}</Typography>
              </Box>
            )}
            {(credits.quota ?? credits.total) !== undefined && (
              <Box sx={{ textAlign: "center", flex: 1, minWidth: 100 }}>
                <Typography variant="h6" fontWeight={700} color="#6366f1">
                  {Number(credits.quota ?? credits.total ?? 0).toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">{t('profile.credits_quota')}</Typography>
              </Box>
            )}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
